// DEPENDENCIES
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";

// API
import { getAPICall, postAPICall } from "../../config/apiCalls";
import { UserContext } from "../../context/UserContext";

// CSS
import "./UnitMiningDashboard.css";

// MUI COMPONENTS
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";

// SHARED TABLE — the same generic table every Admin console and Logs page uses
import TanStackTable from "../Misc/Table/TanStackTable";
import { notApplicable } from "../Misc/Table/TanStackTableConstants";

// MUI ICONS
import RefreshIcon from "@mui/icons-material/Refresh";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ViewListIcon from "@mui/icons-material/ViewList";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LaunchIcon from "@mui/icons-material/Launch";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LayersIcon from "@mui/icons-material/Layers";
import DomainIcon from "@mui/icons-material/Domain";
import PaidIcon from "@mui/icons-material/Paid";
import AutorenewIcon from "@mui/icons-material/Autorenew";

// ── Palette — matches AutomationLogsDashboard / ProjectHealthDashboard ─────────

const BLUE = "#184c93";
const GREEN = "#2e7d32";
const RED = "#c62828";
const YELLOW = "#ca8a04";
const MUTED_COLOR = "#6b6b6b";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_MS = 86400000;
const STALE_AFTER_DAYS = 365;

// J2's contracted ATTOM plan cap — a real config value, not derived from any
// table. There is nowhere in the DB that stores a billing plan's credit
// allotment, so this stays a constant until/unless that becomes configurable.
const CREDIT_ALLOTMENT = 5000;

// ── Real billing period, computed from the actual calendar ─────────────────────
const NOW = new Date();
const periodKey = (y, m) => `${y}-${String(m + 1).padStart(2, "0")}`;
const CUR_Y = NOW.getFullYear(), CUR_M = NOW.getMonth();
const PREV_DATE = new Date(CUR_Y, CUR_M - 1, 1);
const PREV_Y = PREV_DATE.getFullYear(), PREV_M = PREV_DATE.getMonth();
const CURRENT_PERIOD_KEY = periodKey(CUR_Y, CUR_M);
const PREV_PERIOD_KEY = periodKey(PREV_Y, PREV_M);

// The period picker only ever shows the current + previous billing month, so
// there's no reason to pull the full history of unit_mining_api_usage on
// every load — that table only grows (every real ATTOM call adds a row) and
// an unbounded fetch gets slower every month this tool stays in use.
const USAGE_FETCH_SINCE = `${PREV_Y}-${String(PREV_M + 1).padStart(2, "0")}-01`;

const LEADER_PALETTE = ["#184c93", "#2b6cb0", "#6d7e8c", "#4a6b8a", "#7c8b99", "#97a6b2", "#b5b5b5"];

// URL-addressable view/dataset — lets someone link straight to, say,
// ?view=table&dataset=inprogress instead of only ever landing on the default.
const VIEW_QUERY_PARAM = "view";
const DATASET_QUERY_PARAM = "dataset";
const VALID_VIEWS = ["dashboard", "table"];
const VALID_DATASETS = ["submits", "inprogress", "unmined"];
const DEFAULT_VIEW = "dashboard";
const DEFAULT_DATASET = "submits";

// Real unit_mining_units statuses (Needs Review / Enriching / Ready / Discovered
// / Pushed, plus Missing Address / Error from edge cases in the service code)
// collapsed to the wizard's three high-level stages, for the "Stopped At"
// column — whichever stage the least-finished open unit is sitting in.
const STAGE_MAP = {
	Discovered: "Discovery",
	"Missing Address": "Discovery",
	"Needs Review": "Enrichment",
	Enriching: "Enrichment",
	Error: "Enrichment",
	Ready: "Push",
	Pushed: "Push",
};

// ── Formatting helpers ───────────────────────────────────────────────────────────

const fmt = (v) => Math.round(v).toLocaleString("en-US");
const time2 = (v) => String(v).padStart(2, "0");
const stamp = (d) => `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ${time2(d.getHours())}:${time2(d.getMinutes())}`;
const dateOnly = (d) => `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
const initials = (name) => (name || "?").split(" ").filter(Boolean).map((x) => x[0]).join("").slice(0, 2).toUpperCase();
const daysSince = (d) => Math.floor((Date.now() - d.getTime()) / DAY_MS);

// ── Real credit/leaderboard/timing/activity — derived from unit_mining_api_usage ──

function computeUsageMetrics(usageRows, { y, m, elapsed, periodDays, rangeDays }) {
	const inPeriod = usageRows.filter((r) => r.created.getFullYear() === y && r.created.getMonth() === m);
	const firstDay = elapsed - rangeDays + 1;
	const filtered = inPeriod.filter((r) => r.created.getDate() >= firstDay);

	// Credits Used, pace, burn rate, and the projection always reflect the
	// FULL billing period to date — not the "Last 7/14 days" selector, which
	// only narrows the chart, leaderboard, When It's Used, and KPI cards
	// below. Otherwise switching to "Last 7 days" would understate how much
	// of the month you've actually spent.
	const creditsUsed = inPeriod.reduce((a, r) => a + (r.billable ? 1 : 0), 0);
	const usedPct = Math.min((creditsUsed / CREDIT_ALLOTMENT) * 100, 100);
	const burn = elapsed > 0 ? creditsUsed / elapsed : 0;
	const projected = burn * periodDays;
	const pacePct = Math.min((elapsed / periodDays) * 100, 100);
	const over = projected > CREDIT_ALLOTMENT;
	const paceColor = over ? RED : GREEN;
	const capDay = Math.ceil(burn > 0 ? CREDIT_ALLOTMENT / burn : periodDays);
	const periodFailedCalls = inPeriod.filter((r) => !r.success).length;
	const failedCalls = filtered.filter((r) => !r.success).length;

	const dayTotals = [];
	for (let d = 1; d <= elapsed; d++) {
		dayTotals.push({ d, total: filtered.filter((r) => r.created.getDate() === d).reduce((a, r) => a + (r.billable ? 1 : 0), 0) });
	}
	const peak = dayTotals.slice().sort((a, b) => b.total - a.total)[0] || { d: 1, total: 0 };

	const PX0 = 64, PX1 = 700, PY0 = 18, PY1 = 206;
	// Rounding step scales with the plan's own allotment (1/10th of it) rather
	// than a fixed amount, so the axis pads the cap by ~10% regardless of plan
	// size instead of a flat 5000 unit jump that badly overshoots a small cap.
	const yAxisStep = Math.max(Math.round(CREDIT_ALLOTMENT / 10), 1);
	const yMax = Math.max(Math.ceil(Math.max(CREDIT_ALLOTMENT * 1.06, projected * 1.04) / yAxisStep) * yAxisStep, yAxisStep);
	const x = (i) => PX0 + (i / Math.max(periodDays - 1, 1)) * (PX1 - PX0);
	const yPos = (v) => PY1 - (Math.min(v, yMax) / yMax) * (PY1 - PY0);
	let cum = 0;
	const pts = dayTotals.map((t, i) => { cum += t.total; return [x(i), yPos(cum)]; });
	const last = pts[pts.length - 1] || [x(0), yPos(0)];
	const cumLine = pts.length ? pts.map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" ") : `M${x(0)} ${yPos(0)}`;
	const cumArea = pts.length ? cumLine + " L" + last[0].toFixed(1) + " " + PY1 + " L" + pts[0][0].toFixed(1) + " " + PY1 + " Z" : "";
	const paceLine = "M" + x(0).toFixed(1) + " " + yPos(CREDIT_ALLOTMENT / periodDays).toFixed(1) + " L" + x(periodDays - 1).toFixed(1) + " " + yPos(CREDIT_ALLOTMENT).toFixed(1);
	const projLine = "M" + last[0].toFixed(1) + " " + last[1].toFixed(1) + " L" + x(periodDays - 1).toFixed(1) + " " + yPos(projected).toFixed(1);
	const gridLines = [1, 0.75, 0.5, 0.25, 0].map((f) => ({
		y: yPos(yMax * f), label: f === 0 ? "0" : fmt(yMax * f), color: f === 0 ? "#d8d8d8" : "#f2f4f7",
	}));
	const capY = yPos(CREDIT_ALLOTMENT);
	const xTicks = [1, 5, 10, 15, 20, 25, periodDays].filter((d) => d <= periodDays).map((d) => ({ x: x(d - 1), label: String(d) }));

	const byUser = {};
	filtered.forEach((r) => {
		const key = r.employee_name || (r.employee_id != null ? `Employee #${r.employee_id}` : "Unknown");
		const e = byUser[key] || (byUser[key] = { name: key, credits: 0, calls: 0 });
		e.credits += r.billable ? 1 : 0;
		e.calls += 1;
	});
	const leaderList = Object.values(byUser).sort((a, b) => b.credits - a.credits);
	const topCredits = leaderList.length ? leaderList[0].credits : 1;
	const leaders = leaderList.map((u, i) => ({
		name: u.name, initials: initials(u.name), avatarBg: LEADER_PALETTE[i % LEADER_PALETTE.length],
		credits: fmt(u.credits), calls: u.calls,
		share: creditsUsed ? Math.round((u.credits / creditsUsed) * 100) : 0,
		w: (u.credits / topCredits) * 100,
	}));

	const hourBuckets = [];
	let maxHour = 1;
	for (let h = 7; h <= 18; h++) {
		const rs = filtered.filter((r) => r.created.getHours() === h);
		const c = rs.reduce((a, r) => a + (r.billable ? 1 : 0), 0);
		hourBuckets.push({ h, c, n: rs.length });
		if (c > maxHour) maxHour = c;
	}
	const offHours = filtered.filter((r) => r.created.getHours() < 7 || r.created.getHours() > 18);
	const offCredits = offHours.reduce((a, r) => a + (r.billable ? 1 : 0), 0);
	const hours = hourBuckets.map((b) => ({
		label: b.h === 12 ? "12p" : b.h > 12 ? b.h - 12 : b.h + "a",
		h: (b.c / maxHour) * 100,
		color: b.c >= maxHour * 0.72 ? BLUE : "#8fa9c9",
		tip: `${b.h > 12 ? b.h - 12 + " PM" : b.h + " AM"} — ${b.n} calls, ${fmt(b.c)} credits`,
	}));
	const dowTotals = [1, 2, 3, 4, 5, 6, 0].map((d) => {
		const rs = filtered.filter((r) => r.created.getDay() === d);
		return { d, c: rs.reduce((a, r) => a + (r.billable ? 1 : 0), 0), n: rs.length };
	});
	const maxDow = Math.max.apply(null, dowTotals.map((t) => t.c).concat([1]));
	const dows = dowTotals.map((t) => {
		const k = t.c / maxDow;
		return {
			label: DOW[t.d].slice(0, 1) + (t.d === 4 ? "h" : t.d === 6 ? "a" : t.d === 0 ? "u" : ""),
			color: t.c === 0 ? "#f0f2f5" : `rgba(24,76,147,${(0.15 + k * 0.85).toFixed(2)})`,
			tip: `${DOW[t.d]} — ${t.n} calls, ${fmt(t.c)} credits`,
		};
	});
	const busiest = hourBuckets.slice().sort((a, b) => b.c - a.c)[0] || { h: 10 };
	const busiestDow = dowTotals.slice().sort((a, b) => b.c - a.c)[0] || { d: 2 };

	const feed = filtered.slice(0, 25).map((r) => {
		const propLabel = r.property_name || (r.property_id != null ? `Property #${r.property_id}` : "Unknown property");
		const who = r.employee_name || "Unknown user";
		const callLabel = r.call_type === "discovery" ? "Discovery search" : "Enrichment lookup";
		let detail, icon, color;
		if (!r.success) {
			detail = `${who} · ${callLabel} failed.${r.error_message ? " " + r.error_message : ""}`;
			icon = "error"; color = RED;
		} else if (r.billable) {
			detail = `${who} · ${callLabel}${r.call_type === "discovery" && r.result_count != null ? ` · ${fmt(r.result_count)} candidates` : ""}`;
			icon = "check_circle"; color = GREEN;
		} else {
			detail = `${who} · ${callLabel} · no results (not billed)`;
			icon = "info"; color = MUTED_COLOR;
		}
		return { property: propLabel, detail, ts: stamp(r.created), credits: r.billable ? "1 credit" : "0 credits", icon, color };
	});

	const propsTouched = new Set(filtered.map((r) => r.property_id).filter((v) => v != null)).size;
	const enrichmentRows = filtered.filter((r) => r.call_type === "enrichment");
	const unitsEnriched = enrichmentRows.filter((r) => r.billable).length;
	const successRate = filtered.length ? Math.round(((filtered.length - failedCalls) / filtered.length) * 100) : 100;

	return {
		creditsUsedF: fmt(creditsUsed), usedPct, pacePct, paceColor, over,
		paceTargetF: fmt((CREDIT_ALLOTMENT / periodDays) * elapsed),
		burnRateF: fmt(burn), projectedF: fmt(projected),
		exhaustLabel: capDay <= periodDays ? `${MONTHS[m]} ${capDay}` : "Not this period",
		failedCalls, failedCallsPct: filtered.length ? Math.round((failedCalls / filtered.length) * 100) : 0,
		periodFailedCalls, periodFailedCallsPct: inPeriod.length ? Math.round((periodFailedCalls / inPeriod.length) * 100) : 0,
		peakLabel: `${MONTHS[m]} ${peak.d}`, peakCreditsF: fmt(peak.total),
		chartCaption: `Cumulative billable ATTOM calls against the ${fmt(CREDIT_ALLOTMENT)}-credit cap · ${rangeDays >= elapsed ? "full period" : `last ${rangeDays} days`}`,
		cumLine, cumArea, paceLine, projLine, gridLines, xTicks,
		capY, capLabelY: Math.max(capY - 6, 12),
		projLabelY: Math.min(Math.max(yPos(projected) - 8, 13), PY1 - 6),
		cumEndX: last[0], cumEndY: last[1],
		leaders,
		whenCaption: `${filtered.length} calls`,
		whenInsight: filtered.length
			? `Busiest window is ${busiest.h > 12 ? busiest.h - 12 + " PM" : busiest.h + " AM"} on ${DOW[busiestDow.d]}s. ` +
				(offCredits ? `${fmt(offCredits)} credits ran outside 7 AM–7 PM.` : "No off-hours consumption this period.")
			: "No ATTOM usage recorded yet for this period.",
		hours, dows, feed,
		propsTouched, unitsEnriched,
		enrichmentAttempts: enrichmentRows.length,
		successRate,
		avgPerPropertyF: propsTouched ? fmt(creditsUsed / propsTouched) : "0",
		avgPerCallF: filtered.length ? (creditsUsed / filtered.length).toFixed(2) : "0",
	};
}

// ── Real Property Coverage + In Progress + Unmined tables — derived from the
// property roster and unit_mining_units status ─────────────────────────────────

// `coverage` is the fast SQL aggregate from /api/unitMining/coverage (always
// loaded). `properties`/`units` are the full, lazily-loaded detail behind
// the In Progress / Unmined Properties tables — they may still be empty
// when this runs, in which case the row-level tables are simply empty until
// that fetch completes, but the coverage numbers (sourced from `coverage`,
// not from these arrays) are correct immediately regardless.
function computePropertyMetrics(properties, units, usageRows, coverage, { q }) {
	const creditsByProperty = {};
	usageRows.forEach((r) => {
		if (r.property_id == null || !r.billable) return;
		creditsByProperty[r.property_id] = (creditsByProperty[r.property_id] || 0) + 1;
	});

	const byProperty = {};
	units.forEach((u) => {
		const entry = byProperty[u.property_id] || (byProperty[u.property_id] = {
			propertyId: u.property_id, total: 0, unpushed: 0,
			firstActivity: null, lastActivity: null, lastActivityUnit: null,
		});
		entry.total += 1;
		if (u.status !== "Pushed") entry.unpushed += 1;
		if (!entry.firstActivity || u.created < entry.firstActivity) entry.firstActivity = u.created;
		if (!entry.lastActivity || u.modified > entry.lastActivity) { entry.lastActivity = u.modified; entry.lastActivityUnit = u; }
	});

	const propertyById = {};
	properties.forEach((p) => { propertyById[p.id] = p; });

	const minedIds = Object.keys(byProperty).map(Number);
	const totalProperties = coverage ? coverage.totalProperties : properties.length;
	const minedCount = coverage ? coverage.minedCount : minedIds.length;
	const unminedCount = Math.max(totalProperties - minedCount, 0);
	const coveragePct = totalProperties ? Math.round((minedCount / totalProperties) * 100) : 0;

	// Finished = the property has units on record at all. Doesn't consider push
	// status or recency — that's what In Progress / Stale break out separately.
	// Always equal to minedCount by that definition.
	const finishedCount = minedCount;

	const staleEntries = [];
	const inProgressEntries = [];
	minedIds.forEach((pid) => {
		const entry = byProperty[pid];
		if (entry.unpushed > 0) {
			inProgressEntries.push(entry);
		} else if (daysSince(entry.lastActivity) > STALE_AFTER_DAYS) {
			staleEntries.push(entry);
		}
	});

	const query = q.trim().toLowerCase();
	const match = (row) => !query || Object.keys(row).some((k) => String(row[k]).toLowerCase().indexOf(query) >= 0);

	// "Stopped At" — the stage of whichever open unit was touched most recently,
	// a reasonable proxy for where a resumable run is actually stuck.
	const stageFor = (pid) => {
		const openUnits = units.filter((u) => u.property_id === pid && u.status !== "Pushed");
		const mostRecent = openUnits.slice().sort((a, b) => b.modified - a.modified)[0];
		return mostRecent ? (STAGE_MAP[mostRecent.status] || mostRecent.status) : "Push";
	};

	const ipRows = inProgressEntries.map((entry) => {
		const p = propertyById[entry.propertyId] || {};
		return {
			id: `P${entry.propertyId}`,
			propertyId: entry.propertyId,
			property: p.name || `Property #${entry.propertyId}`,
			city: [p.city, p.state].filter(Boolean).join(", "),
			user: entry.lastActivityUnit?.modified_by_name || "Unknown",
			stage: stageFor(entry.propertyId),
			total: entry.total,
			unpushed: entry.unpushed,
			credits: creditsByProperty[entry.propertyId] || 0,
			started: stamp(entry.firstActivity),
			startedTs: entry.firstActivity.getTime(),
			lastActivity: stamp(entry.lastActivity),
			lastActivityTs: entry.lastActivity.getTime(),
			idleDays: daysSince(entry.lastActivity),
		};
	}).filter(match).sort((a, b) => b.idleDays - a.idleDays);

	const staleRows = staleEntries.map((entry) => {
		const p = propertyById[entry.propertyId] || {};
		return {
			id: `S${entry.propertyId}`,
			propertyId: entry.propertyId,
			property: p.name || `Property #${entry.propertyId}`,
			city: [p.city, p.state].filter(Boolean).join(", "),
			added: p.created ? dateOnly(p.created) : "—",
			addedTs: p.created ? p.created.getTime() : 0,
			status: "Stale",
		};
	});
	const neverMinedRows = properties.filter((p) => !byProperty[p.id]).map((p) => ({
		id: `N${p.id}`,
		propertyId: p.id,
		property: p.name,
		city: [p.city, p.state].filter(Boolean).join(", "),
		added: p.created ? dateOnly(p.created) : "—",
		addedTs: p.created ? p.created.getTime() : 0,
		status: "Never Mined",
	}));
	const unminedRows = [...staleRows, ...neverMinedRows].filter(match);

	const staleCountValue = coverage ? coverage.staleCount : staleEntries.length;
	const ipCountValue = coverage ? coverage.inProgressCount : inProgressEntries.length;

	return {
		minedCountF: fmt(minedCount), finishedCountF: fmt(finishedCount), staleCountF: fmt(staleCountValue), unminedCountF: fmt(unminedCount),
		coveragePct, stalePctW: totalProperties ? (staleCountValue / totalProperties) * 100 : 0,
		ipCount: ipCountValue,
		// Credits spent on in-progress properties still needs the detailed
		// per-property breakdown (properties/units), so this stays 0 until
		// that lazily-loaded data arrives — the count above doesn't wait on it.
		ipCreditsF: fmt(inProgressEntries.reduce((a, e) => a + (creditsByProperty[e.propertyId] || 0), 0)),
		ipRows, unminedRows,
		tableFootnote: "A property counts as In Progress while it has any unit not yet pushed to HubSpot. \"Stopped At\" is the stage of whichever open unit was touched most recently.",
		unminedFootnote: `Properties in the Property Console with no completed Unit Mining run, or none in the last ${STALE_AFTER_DAYS} days.`,
	};
}

// ── Submit Log — one row per real ATTOM call, from unit_mining_api_usage ──────

function computeSubmitLog(usageRows, { q }) {
	const query = q.trim().toLowerCase();
	const match = (row) => !query || Object.keys(row).some((k) => String(row[k]).toLowerCase().indexOf(query) >= 0);
	const rows = usageRows.map((r) => ({
		id: r.id,
		ts: r.created.getTime(),
		when: stamp(r.created),
		user: r.employee_name || "Unknown",
		propertyId: r.property_id,
		property: r.property_name || (r.property_id != null ? `Property #${r.property_id}` : "—"),
		city: [r.property_city, r.property_state].filter(Boolean).join(", "),
		callType: r.call_type === "discovery" ? "Discovery" : "Enrichment",
		resultCount: r.call_type === "discovery" && r.result_count != null ? r.result_count : null,
		credits: r.billable ? 1 : 0,
		status: !r.success ? "Failed" : r.billable ? "Billed" : "No Results",
		error: r.error_message || "",
	})).filter(match);
	return { rows, count: rows.length };
}

// ── Small presentational pieces ─────────────────────────────────────────────────

// Mirrors AutomationLogsDashboard's status cell: a colored dot + colored label,
// the established convention on these pages for a row's status (as opposed to a
// filled Chip pill, which isn't used for this purpose on either sibling page).
function StatusDot({ color, label }) {
	return (
		<span className="unitMiningDashboardStatusCell">
			<span className="unitMiningDashboardStatusDot" style={{ background: color }} />
			<span style={{ color }}>{label}</span>
		</span>
	);
}

function submitLogStatusDot(status) {
	if (status === "Failed") return <StatusDot color={RED} label="Failed" />;
	if (status === "Billed") return <StatusDot color={GREEN} label="Billed" />;
	return <StatusDot color="#9ca3af" label="No Results" />;
}

function stageDot(stage) {
	return <StatusDot color={stage === "Push" ? BLUE : YELLOW} label={stage} />;
}

function unminedStatusDot(status) {
	return status === "Stale"
		? <StatusDot color={YELLOW} label="Stale" />
		: <StatusDot color="#9ca3af" label="Never Mined" />;
}

// TanStackTable renders every cell left-aligned, so numeric columns align
// themselves within the cell instead.
function RightAlign({ children }) {
	return <div style={{ textAlign: "right" }}>{children}</div>;
}

// Mirrors ProjectHealthDashboard's .phdProjectLink: a plain-looking cell that
// turns blue with a launch icon on hover, rather than a wall of blue links.
function PropertyCell({ property, propertyId }) {
	return propertyId != null ? (
		<a
			className="unitMiningDashboardPropertyLink"
			href={`/properties/viewer?recordId=${encodeURIComponent(propertyId)}`}
			target="_blank"
			rel="noreferrer"
		>
			{property}
			<LaunchIcon className="unitMiningDashboardLaunchIcon" fontSize="inherit" />
		</a>
	) : (
		<span className="unitMiningDashboardTableCellPrimary">{property}</span>
	);
}

// ── Table columns — shared TanStackTable column definitions ────────────────────

const SUBMIT_LOG_COLUMNS = [
	{ accessorKey: "ts", header: "Date / Time", cell: ({ row }) => row.original.when },
	{ accessorKey: "user", header: "Operator", cell: ({ row }) => row.original.user },
	{ accessorKey: "property", header: "Property", cell: ({ row }) => <PropertyCell property={row.original.property} propertyId={row.original.propertyId} /> },
	{ accessorKey: "callType", header: "Call Type", cell: ({ row }) => row.original.callType },
	{ accessorKey: "resultCount", header: "Result Count", cell: ({ row }) => <RightAlign>{row.original.resultCount != null ? fmt(row.original.resultCount) : notApplicable}</RightAlign> },
	{ accessorKey: "credits", header: "Credits", cell: ({ row }) => <RightAlign>{row.original.credits}</RightAlign> },
	{ accessorKey: "status", header: "Status", cell: ({ row }) => submitLogStatusDot(row.original.status) },
	{
		accessorKey: "error", header: "Failure Detail", enableSorting: false,
		cell: ({ row }) => row.original.error ? <span style={{ color: RED }}>{row.original.error}</span> : notApplicable,
	},
];

const IN_PROGRESS_COLUMNS = [
	{ accessorKey: "property", header: "Property", cell: ({ row }) => <PropertyCell property={row.original.property} propertyId={row.original.propertyId} /> },
	{ accessorKey: "user", header: "Last Touched By", cell: ({ row }) => row.original.user },
	{ accessorKey: "stage", header: "Stopped At", cell: ({ row }) => stageDot(row.original.stage) },
	{ accessorKey: "total", header: "Total Units", cell: ({ row }) => <RightAlign>{fmt(row.original.total)}</RightAlign> },
	{ accessorKey: "unpushed", header: "Unpushed", cell: ({ row }) => <RightAlign>{fmt(row.original.unpushed)}</RightAlign> },
	{ accessorKey: "credits", header: "Credits Spent", cell: ({ row }) => <RightAlign>{fmt(row.original.credits)}</RightAlign> },
	{ accessorKey: "startedTs", header: "Started", cell: ({ row }) => row.original.started },
	{ accessorKey: "lastActivityTs", header: "Last Activity", cell: ({ row }) => row.original.lastActivity },
	{
		accessorKey: "idleDays", header: "Idle",
		cell: ({ row }) => (
			<RightAlign>
				<span style={{ fontWeight: 600, color: row.original.idleDays >= 14 ? RED : row.original.idleDays >= 7 ? YELLOW : MUTED_COLOR }}>
					{row.original.idleDays}d
				</span>
			</RightAlign>
		),
	},
];

const UNMINED_COLUMNS = [
	{ accessorKey: "property", header: "Property", cell: ({ row }) => <PropertyCell property={row.original.property} propertyId={row.original.propertyId} /> },
	{ accessorKey: "addedTs", header: "Added To Console", cell: ({ row }) => row.original.added },
	{ accessorKey: "status", header: "Status", cell: ({ row }) => unminedStatusDot(row.original.status) },
];

const toggleGroupSx = {
	height: 36,
	"& .MuiToggleButton-root": {
		height: 36,
		boxSizing: "border-box",
		textTransform: "none",
		color: "text.secondary",
		borderColor: "#e0e0e0",
	},
	"& .MuiToggleButton-root.Mui-selected": {
		backgroundColor: "primary.main",
		color: "#fff",
	},
	"& .MuiToggleButton-root.Mui-selected:hover": {
		backgroundColor: "primary.main",
	},
};

// ── Main component ──────────────────────────────────────────────────────────────

const UnitMiningDashboard = () => {
	const { instance, accounts } = useMsal();
	const employeeID = useContext(UserContext);

	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Unit Mining Dashboard",
			employeeID: employeeID,
			notes: "Accessed the Unit Mining Dashboard",
		}).catch(() => {});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const [searchParams, setSearchParams] = useSearchParams();
	const viewFromQuery = searchParams.get(VIEW_QUERY_PARAM) || "";
	const view = VALID_VIEWS.includes(viewFromQuery) ? viewFromQuery : DEFAULT_VIEW;
	const datasetFromQuery = searchParams.get(DATASET_QUERY_PARAM) || "";
	const dataset = VALID_DATASETS.includes(datasetFromQuery) ? datasetFromQuery : DEFAULT_DATASET;

	const setView = useCallback((nextView) => {
		const nextParams = new URLSearchParams(searchParams);
		nextParams.set(VIEW_QUERY_PARAM, nextView);
		setSearchParams(nextParams, { replace: true });
	}, [searchParams, setSearchParams]);

	const setDataset = useCallback((nextDataset) => {
		const nextParams = new URLSearchParams(searchParams);
		nextParams.set(VIEW_QUERY_PARAM, "table");
		nextParams.set(DATASET_QUERY_PARAM, nextDataset);
		setSearchParams(nextParams, { replace: true });
	}, [searchParams, setSearchParams]);

	const [usageRows, setUsageRows] = useState([]);
	const [coverage, setCoverage] = useState(null);
	const [properties, setProperties] = useState([]);
	const [units, setUnits] = useState([]);
	const [loading, setLoading] = useState(true);
	const [detailLoading, setDetailLoading] = useState(false);
	const [detailLoaded, setDetailLoaded] = useState(false);
	const [loadError, setLoadError] = useState("");
	const [lastUpdated, setLastUpdated] = useState(null);

	// Fast path, always fetched: the (date-bounded) usage log plus a five-number
	// SQL aggregate for coverage. This alone powers the entire Dashboard view —
	// it never touches the full properties/unit_mining_units tables, so page
	// load stays fast no matter how large the portfolio gets.
	const fetchDashboardData = useCallback(async () => {
		setLoading(true);
		setLoadError("");
		try {
			const [usageRes, coverageRes] = await Promise.all([
				getAPICall(instance, accounts[0], "/api/unitMining/apiUsage", { since: USAGE_FETCH_SINCE }),
				getAPICall(instance, accounts[0], "/api/unitMining/coverage"),
			]);
			setUsageRows((usageRes.data?.usage || []).map((r) => ({ ...r, created: new Date(r.created) })));
			setCoverage(coverageRes.data || null);
			setLastUpdated(new Date());
		} catch (err) {
			setLoadError(err?.response?.data?.error || err.message || "Failed to load Unit Mining data.");
		} finally {
			setLoading(false);
		}
	}, [instance, accounts]);

	useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

	// Slow path, lazy: the full property/unit detail behind the In Progress and
	// Unmined Properties tables. Only fetched once someone actually opens the
	// Submit Data view — not on every dashboard visit — since this ships every
	// row in both tables and will only get more expensive as the portfolio grows.
	const fetchPropertyDetail = useCallback(async () => {
		setDetailLoading(true);
		try {
			const statusRes = await getAPICall(instance, accounts[0], "/api/unitMining/propertyStatus");
			setProperties((statusRes.data?.properties || []).map((p) => ({ ...p, created: p.created ? new Date(p.created) : null })));
			setUnits((statusRes.data?.units || []).map((u) => ({ ...u, created: new Date(u.created), modified: new Date(u.modified) })));
		} catch (err) {
			setLoadError(err?.response?.data?.error || err.message || "Failed to load property detail.");
		} finally {
			// Set on failure too, not just success — otherwise the effect below
			// (guarded on !detailLoaded && !detailLoading) fires again the instant
			// detailLoading flips back to false, retrying in a tight loop on any
			// error instead of surfacing it. A failed attempt still counts as
			// "attempted"; the manual Refresh button is the retry path.
			setDetailLoaded(true);
			setDetailLoading(false);
		}
	}, [instance, accounts]);

	useEffect(() => {
		if (view === "table" && !detailLoaded && !detailLoading) fetchPropertyDetail();
	}, [view, detailLoaded, detailLoading, fetchPropertyDetail]);

	const handleRefresh = () => {
		fetchDashboardData();
		if (detailLoaded) fetchPropertyDetail();
	};

	const [period, setPeriod] = useState(CURRENT_PERIOD_KEY);
	const [range, setRange] = useState("full");
	const [q, setQ] = useState("");

	const usage = useMemo(() => {
		const isCurrent = period === CURRENT_PERIOD_KEY;
		const y = isCurrent ? CUR_Y : PREV_Y;
		const m = isCurrent ? CUR_M : PREV_M;
		const periodDays = new Date(y, m + 1, 0).getDate();
		const elapsed = isCurrent ? NOW.getDate() : periodDays;
		const rangeDays = range === "full" ? elapsed : Math.min(parseInt(range, 10), elapsed);
		return computeUsageMetrics(usageRows, { y, m, elapsed, periodDays, rangeDays });
	}, [usageRows, period, range]);

	const propertyStatus = useMemo(() => computePropertyMetrics(properties, units, usageRows, coverage, { q }), [properties, units, usageRows, coverage, q]);
	const submitLog = useMemo(() => computeSubmitLog(usageRows, { q }), [usageRows, q]);

	const periodLabel = period === CURRENT_PERIOD_KEY ? MONTHS[CUR_M] : MONTHS[PREV_M];
	const periodDays = period === CURRENT_PERIOD_KEY ? new Date(CUR_Y, CUR_M + 1, 0).getDate() : new Date(PREV_Y, PREV_M + 1, 0).getDate();
	const dayOfPeriod = period === CURRENT_PERIOD_KEY ? NOW.getDate() : periodDays;

	const goUnmined = () => setDataset("unmined");
	const goSubmitData = () => setDataset("submits");

	const kpis = [
		{
			label: "Units Enriched", icon: LayersIcon, tint: BLUE,
			value: fmt(usage.unitsEnriched), sub: `${fmt(usage.enrichmentAttempts)} lookups attempted`, subColor: MUTED_COLOR,
		},
		{
			label: "Properties Touched", icon: DomainIcon, tint: BLUE,
			value: fmt(usage.propsTouched), sub: `${usage.leaders.length} operators active`, subColor: MUTED_COLOR,
		},
		{
			label: "Success Rate", icon: CheckCircleIcon,
			tint: usage.successRate >= 95 ? GREEN : usage.successRate >= 88 ? YELLOW : RED,
			value: `${usage.successRate}%`, sub: `${fmt(usage.failedCalls)} failed calls`, subColor: MUTED_COLOR,
		},
		{
			label: "Avg Credits / Property", icon: PaidIcon, tint: BLUE,
			value: usage.avgPerPropertyF, sub: `${usage.avgPerCallF} credits per call`, subColor: MUTED_COLOR,
		},
		{
			label: "Mining In Progress", icon: AutorenewIcon, tint: BLUE,
			value: fmt(propertyStatus.ipCount), sub: `${propertyStatus.ipCreditsF} credits spent, not pushed`, subColor: MUTED_COLOR,
			onLinkClick: () => setDataset("inprogress"),
		},
	];

	const datasetInfoNote = dataset === "inprogress"
		? propertyStatus.tableFootnote
		: dataset === "unmined"
			? propertyStatus.unminedFootnote
			: "Every ATTOM call this tool has logged. It isn't filtered to a billing period, since each call is only ever logged once.";

	const periodRangeControls = (
		<>
			<Select size="small" value={period} onChange={(e) => setPeriod(e.target.value)} sx={{ width: 260 }}>
				<MenuItem value={CURRENT_PERIOD_KEY}>{MONTHS[CUR_M]} {CUR_Y} billing period (current)</MenuItem>
				<MenuItem value={PREV_PERIOD_KEY}>{MONTHS[PREV_M]} {PREV_Y} billing period</MenuItem>
			</Select>
			<Select size="small" value={range} onChange={(e) => setRange(e.target.value)} sx={{ width: 180 }}>
				<MenuItem value="full">Full billing period</MenuItem>
				<MenuItem value="14">Last 14 days</MenuItem>
				<MenuItem value="7">Last 7 days</MenuItem>
			</Select>
		</>
	);

	return (
		<div className="UnitMiningDashboard">
			<div className="unitMiningDashboardHeader">
				<div className="unitMiningDashboardHeaderRow">
					<div className="unitMiningDashboardHeaderTitle">Unit Mining Dashboard</div>
					<div className="unitMiningDashboardHeaderActions">
						{loading && <CircularProgress size={16} />}
						{lastUpdated && <span className="unitMiningDashboardUpdatedText">Updated {stamp(lastUpdated)}</span>}
						<Tooltip title="Refresh">
							<span>
								<IconButton size="small" onClick={handleRefresh} disabled={loading}>
									<RefreshIcon />
								</IconButton>
							</span>
						</Tooltip>
						<ToggleButtonGroup
							size="small"
							value={view}
							exclusive
							onChange={(e, value) => value && setView(value)}
							sx={toggleGroupSx}
						>
							<ToggleButton value="dashboard">
								<DashboardIcon fontSize="small" style={{ marginRight: 6 }} />
								Dashboard
							</ToggleButton>
							<ToggleButton value="table">
								<ViewListIcon fontSize="small" style={{ marginRight: 6 }} />
								Submit Data
							</ToggleButton>
						</ToggleButtonGroup>
					</div>
				</div>
			</div>

			{loadError && <Alert severity="error" sx={{ mx: 3 }}>{loadError}</Alert>}

			{view === "dashboard" && (
				<div className="unitMiningDashboardBody">

					{/* ── Toolbar ── */}
					<div className="unitMiningDashboardToolbar">
						{periodRangeControls}
						<div className="unitMiningDashboardToolbarSpacer" />
						<Tooltip
							title="Tracking started when this feature launched, so earlier billing periods show zero even though calls happened then. It also only counts calls made through this tool. Usage from other integrations or manual API testing won't appear here, so actual ATTOM billing may run higher than shown."
							placement="bottom-end"
							arrow
						>
							<span className="unitMiningDashboardInfoNote">
								<InfoOutlinedIcon sx={{ fontSize: 15, color: "#9ca3af" }} />
								About this data
							</span>
						</Tooltip>
					</div>

					{/* ── KPI grid ── */}
					<div className="unitMiningDashboardKpiGrid">
						{kpis.map((k) => {
							const KpiIcon = k.icon;
							return (
								<div className="unitMiningDashboardKpiCard" key={k.label}>
									<div className="unitMiningDashboardKpiLabel">
										<KpiIcon style={{ fontSize: 16, color: k.tint }} />
										{k.label}
										{k.onLinkClick && (
											<Tooltip title="Open In Progress runs" arrow>
												<IconButton size="small" onClick={k.onLinkClick} sx={{ ml: "auto", p: 0.25, color: BLUE }}>
													<OpenInNewIcon sx={{ fontSize: 16 }} />
												</IconButton>
											</Tooltip>
										)}
									</div>
									<div className="unitMiningDashboardKpiValue" style={{ color: k.tint }}>{k.value}</div>
									<div className="unitMiningDashboardKpiSub" style={{ color: k.subColor }}>{k.sub}</div>
								</div>
							);
						})}
					</div>

					{/* ── Credits used + coverage ── */}
					<div className="unitMiningDashboardGridHero">
						<div className="unitMiningDashboardCard">
							<div className="unitMiningDashboardCardTopRow">
								<div className="unitMiningDashboardFlexCol">
									<span className="unitMiningDashboardEyebrow">Credits Used · {periodLabel} Billing Period</span>
									<div className="unitMiningDashboardBaseline">
										<span className="unitMiningDashboardBigNum">{usage.creditsUsedF}</span>
										<span className="unitMiningDashboardBigNumSub">of {fmt(CREDIT_ALLOTMENT)} credits</span>
									</div>
								</div>
								<div className="unitMiningDashboardFlexCol" style={{ alignItems: "flex-end", gap: 6 }}>
									<StatusDot color={usage.paceColor} label={usage.over ? "Over pace" : "On pace"} />
									<span className="unitMiningDashboardMutedSmall">Day {dayOfPeriod} of {periodDays}</span>
								</div>
							</div>

							<div className="unitMiningDashboardFlexCol" style={{ gap: 8 }}>
								<div className="unitMiningDashboardProgressTrack">
									<div className="unitMiningDashboardProgressFill" style={{ width: `${usage.usedPct}%`, background: usage.paceColor }} />
								</div>
								<div className="unitMiningDashboardPaceMarkerRow">
									<div className="unitMiningDashboardPaceMarker" style={{ left: `${Math.min(Math.max(usage.pacePct, 6), 94)}%` }}>
										<span className="unitMiningDashboardPaceTick" />
										<span className="unitMiningDashboardPaceTickLabel">on-pace {usage.paceTargetF}</span>
									</div>
								</div>
							</div>

							<div className="unitMiningDashboardMiniStatRow">
								<div className="unitMiningDashboardMiniStat">
									<span className="unitMiningDashboardMiniStatLabel">Burn Rate</span>
									<span className="unitMiningDashboardMiniStatValue">{usage.burnRateF}<span className="unitMiningDashboardMiniStatUnit"> /day</span></span>
								</div>
								<div className="unitMiningDashboardMiniStat">
									<span className="unitMiningDashboardMiniStatLabel">
										Projected Month-End
										<Tooltip title="Today's average credits per day (credits used so far ÷ days elapsed this period), carried forward across every day in the billing period. It's a straight-line estimate from your current pace, not a forecast that accounts for upcoming bursts or slow spells." placement="top" arrow>
											<InfoOutlinedIcon sx={{ fontSize: 13, color: "#9ca3af", ml: "4px", verticalAlign: "middle" }} />
										</Tooltip>
									</span>
									<span className="unitMiningDashboardMiniStatValue" style={{ color: usage.paceColor }}>{usage.projectedF}</span>
								</div>
								<div className="unitMiningDashboardMiniStat">
									<span className="unitMiningDashboardMiniStatLabel">Cap Reached</span>
									<span className="unitMiningDashboardMiniStatValue">{usage.exhaustLabel}</span>
								</div>
								<div className="unitMiningDashboardMiniStat">
									<span className="unitMiningDashboardMiniStatLabel">Failed Calls</span>
									<span className="unitMiningDashboardMiniStatValue">{fmt(usage.periodFailedCalls)}<span className="unitMiningDashboardMiniStatUnit"> ({usage.periodFailedCallsPct}%)</span></span>
								</div>
							</div>
						</div>

						<div className="unitMiningDashboardCard">
							<span className="unitMiningDashboardEyebrow">Property Coverage</span>
							<div className="unitMiningDashboardBaseline">
								<span className="unitMiningDashboardBigNum">{propertyStatus.coveragePct}%</span>
								<span className="unitMiningDashboardBigNumSub">of properties mined</span>
							</div>
							<div className="unitMiningDashboardCoverageBar">
								<div style={{ width: `${propertyStatus.coveragePct}%`, background: BLUE }} />
								<div style={{ width: `${propertyStatus.stalePctW}%`, background: YELLOW }} />
							</div>
							<div className="unitMiningDashboardStatLines">
								<div className="unitMiningDashboardStatLine"><span>Mined at least once</span><span className="unitMiningDashboardStatLineVal">{propertyStatus.minedCountF}</span></div>
								<div className="unitMiningDashboardStatLine"><span>Mining finished</span><span className="unitMiningDashboardStatLineVal" style={{ color: GREEN }}>{propertyStatus.finishedCountF}</span></div>
								<div className="unitMiningDashboardStatLine"><span>Stale (no activity &gt; 12 mo)</span><span className="unitMiningDashboardStatLineVal" style={{ color: YELLOW }}>{propertyStatus.staleCountF}</span></div>
								<div className="unitMiningDashboardStatLine"><span>Mining in progress</span><span className="unitMiningDashboardStatLineVal" style={{ color: BLUE }}>{fmt(propertyStatus.ipCount)}</span></div>
								<div className="unitMiningDashboardStatLine"><span>Never mined</span><span className="unitMiningDashboardStatLineVal">{propertyStatus.unminedCountF}</span></div>
							</div>
							<div style={{ flex: 1 }} />
							<Button
								variant="outlined"
								size="small"
								fullWidth
								onClick={goUnmined}
								endIcon={<ArrowForwardIcon sx={{ fontSize: 17 }} />}
							>
								View Unmined
							</Button>
						</div>
					</div>

					{/* ── Chart + recent activity ── */}
					<div className="unitMiningDashboardGrid2">
						<div className="unitMiningDashboardCard unitMiningDashboardChartActivityCard">
							<div className="unitMiningDashboardCardTopRow">
								<div>
									<div className="unitMiningDashboardCardTitle">Credit Consumption Over Time</div>
									<div className="unitMiningDashboardCardSub">{usage.chartCaption}</div>
								</div>
								<div className="unitMiningDashboardFlexCol" style={{ alignItems: "flex-end", gap: 2 }}>
									<span className="unitMiningDashboardMutedSmall">Projected month-end</span>
									<span className="unitMiningDashboardCardTitle" style={{ fontSize: 15, color: usage.paceColor }}>{usage.projectedF} credits</span>
								</div>
							</div>

							<svg viewBox="0 0 720 246" style={{ width: "100%", height: 246, display: "block" }}>
								{usage.gridLines.map((g, i) => (
									<g key={i}>
										<line x1={64} y1={g.y} x2={700} y2={g.y} stroke={g.color} strokeWidth={1} />
										<text x={54} y={g.y} textAnchor="end" dominantBaseline="middle" fontSize={10.5} fill="#8a8a8a">{g.label}</text>
									</g>
								))}
								{usage.xTicks.map((t, i) => (
									<text key={i} x={t.x} y={224} textAnchor="middle" fontSize={10.5} fill="#8a8a8a">{t.label}</text>
								))}
								<line x1={64} y1={usage.capY} x2={700} y2={usage.capY} stroke="#f44336" strokeWidth={1} strokeDasharray="5 4" />
								<text x={700} y={usage.capLabelY} textAnchor="end" fontSize={10.5} fill="#f44336">Cap {fmt(CREDIT_ALLOTMENT)}</text>
								<path d={usage.paceLine} fill="none" stroke="#b5b5b5" strokeWidth={1.5} strokeDasharray="5 4" />
								{usage.cumArea && <path d={usage.cumArea} fill="rgba(24,76,147,0.09)" />}
								<path d={usage.cumLine} fill="none" stroke={BLUE} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
								<path d={usage.projLine} fill="none" stroke={usage.paceColor} strokeWidth={2} strokeDasharray="4 4" />
								<circle cx={usage.cumEndX} cy={usage.cumEndY} r={4} fill={BLUE} />
								<text x={700} y={usage.projLabelY} textAnchor="end" fontSize={10.5} fill={usage.paceColor}>Projected {usage.projectedF}</text>
								<text x={382} y={242} textAnchor="middle" fontSize={10.5} fill="#b5b5b5">Day of {periodLabel} billing period</text>
							</svg>

							<div className="unitMiningDashboardChartLegend">
								<span><span className="unitMiningDashboardLegendSwatch" style={{ background: BLUE }} />Credits used to date</span>
								<span><span className="unitMiningDashboardLegendSwatch unitMiningDashboardLegendSwatch-thin" style={{ background: "#b5b5b5" }} />On-pace budget</span>
								<span><span className="unitMiningDashboardLegendSwatch unitMiningDashboardLegendSwatch-thin" style={{ background: usage.paceColor }} />Projection at current burn</span>
								<div style={{ flex: 1 }} />
								<span>Peak day {usage.peakLabel} · {usage.peakCreditsF} credits</span>
							</div>
						</div>

						<div className="unitMiningDashboardCard unitMiningDashboardChartActivityCard">
							<div className="unitMiningDashboardCardTitle">Recent Activity</div>
							{usage.feed.length === 0 ? (
								<div className="unitMiningDashboardMutedSmall">No ATTOM usage recorded yet for this period.</div>
							) : (
								<div className="unitMiningDashboardActivityFeed">
									{usage.feed.map((f, i) => (
										<div key={i} className="unitMiningDashboardActivityRow">
											{f.icon === "error" && <ErrorIcon sx={{ fontSize: 19, color: f.color, mt: "1px" }} />}
											{f.icon === "check_circle" && <CheckCircleIcon sx={{ fontSize: 19, color: f.color, mt: "1px" }} />}
											{f.icon === "info" && <InfoOutlinedIcon sx={{ fontSize: 19, color: f.color, mt: "1px" }} />}
											<div className="unitMiningDashboardActivityBody">
												<span className="unitMiningDashboardActivityProp">{f.property}</span>
												<span className="unitMiningDashboardMutedSmall">{f.detail}</span>
												<span className="unitMiningDashboardActivityTs">{f.ts}</span>
											</div>
											<span className="unitMiningDashboardActivityCredits">{f.credits}</span>
										</div>
									))}
								</div>
							)}
							<Button variant="text" size="small" onClick={goSubmitData} sx={{ alignSelf: "flex-start" }}>
								See all calls
							</Button>
						</div>
					</div>

					{/* ── Leaderboard + when-it's-used ── */}
					<div className="unitMiningDashboardGrid2">
						<div className="unitMiningDashboardCard unitMiningDashboardLeaderWhenCard">
							<div className="unitMiningDashboardBaseline" style={{ justifyContent: "space-between" }}>
								<div className="unitMiningDashboardCardTitle">Who's Using It</div>
								<span className="unitMiningDashboardMutedSmall">Credits · calls · share of period</span>
							</div>
							{usage.leaders.length === 0 ? (
								<div className="unitMiningDashboardMutedSmall">No ATTOM usage recorded yet for this period.</div>
							) : (
								<div className="unitMiningDashboardFlexCol unitMiningDashboardLeaderFeed" style={{ gap: 12 }}>
									{usage.leaders.map((u, i) => (
										<div key={i} className="unitMiningDashboardLeaderRow">
											<Avatar sx={{ bgcolor: u.avatarBg, width: 32, height: 32, fontSize: 12, fontWeight: 500 }}>{u.initials}</Avatar>
											<div className="unitMiningDashboardLeaderBody">
												<div className="unitMiningDashboardLeaderTopLine">
													<span className="unitMiningDashboardLeaderName">{u.name}</span>
													<span className="unitMiningDashboardLeaderStats"><b>{u.credits}</b> · {u.calls} calls · {u.share}%</span>
												</div>
												<div className="unitMiningDashboardLeaderTrack">
													<div style={{ width: `${u.w}%`, background: u.avatarBg }} />
												</div>
											</div>
										</div>
									))}
								</div>
							)}
						</div>

						<div className="unitMiningDashboardCard unitMiningDashboardLeaderWhenCard">
							<div className="unitMiningDashboardBaseline" style={{ justifyContent: "space-between" }}>
								<div className="unitMiningDashboardCardTitle">When It's Used</div>
								<span className="unitMiningDashboardMutedSmall">{usage.whenCaption}</span>
							</div>
							<div className="unitMiningDashboardFlexCol" style={{ gap: 8 }}>
								<span className="unitMiningDashboardEyebrow">By hour of day</span>
								<div className="unitMiningDashboardHourChart">
									{usage.hours.map((h, i) => (
										<Tooltip key={i} title={h.tip} arrow>
											<div className="unitMiningDashboardHourCol">
												<div className="unitMiningDashboardHourBar" style={{ height: `${h.h}%`, background: h.color }} />
												<span className="unitMiningDashboardHourLabel">{h.label}</span>
											</div>
										</Tooltip>
									))}
								</div>
							</div>
							<div className="unitMiningDashboardFlexCol" style={{ gap: 8 }}>
								<span className="unitMiningDashboardEyebrow">By day of week</span>
								<div className="unitMiningDashboardDowChart">
									{usage.dows.map((w, i) => (
										<Tooltip key={i} title={w.tip} arrow>
											<div className="unitMiningDashboardDowCol">
												<div className="unitMiningDashboardDowBar" style={{ background: w.color }} />
												<span className="unitMiningDashboardDowLabel">{w.label}</span>
											</div>
										</Tooltip>
									))}
								</div>
							</div>
							<div className="unitMiningDashboardInsight">{usage.whenInsight}</div>
						</div>
					</div>
				</div>
			)}

			{view === "table" && (
				<div className="unitMiningDashboardTableBody">
					<div className="unitMiningDashboardToolbar">
						<ToggleButtonGroup
							size="small"
							value={dataset}
							exclusive
							onChange={(e, value) => value && setDataset(value)}
						>
							<ToggleButton value="submits">Submit Log ({submitLog.count})</ToggleButton>
							<ToggleButton value="inprogress">In Progress ({fmt(propertyStatus.ipCount)})</ToggleButton>
							<ToggleButton value="unmined">Unmined Properties ({propertyStatus.unminedCountF})</ToggleButton>
						</ToggleButtonGroup>
						<div className="unitMiningDashboardToolbarSpacer" />
						<Tooltip title={datasetInfoNote} placement="bottom-end" arrow>
							<span className="unitMiningDashboardInfoNote">
								<InfoOutlinedIcon sx={{ fontSize: 15, color: "#9ca3af" }} />
								About this data
							</span>
						</Tooltip>
						<TextField
							size="small"
							value={q}
							onChange={(e) => setQ(e.target.value)}
							placeholder={dataset === "unmined" ? "Search properties…" : "Search…"}
							sx={{ width: 240 }}
						/>
					</div>

					<div className="unitMiningDashboardTableWrap">
						{dataset === "submits" && (
							<TanStackTable
								columnData={SUBMIT_LOG_COLUMNS}
								rowData={submitLog.rows}
								showSearch={false}
								defaultSort={[{ id: "ts", desc: true }]}
							/>
						)}

						{dataset === "inprogress" && (
							detailLoading && !detailLoaded ? (
								<div className="unitMiningDashboardDetailLoading"><CircularProgress size={24} /></div>
							) : (
								<TanStackTable
									columnData={IN_PROGRESS_COLUMNS}
									rowData={propertyStatus.ipRows}
									showSearch={false}
									defaultSort={[{ id: "idleDays", desc: true }]}
								/>
							)
						)}

						{dataset === "unmined" && (
							detailLoading && !detailLoaded ? (
								<div className="unitMiningDashboardDetailLoading"><CircularProgress size={24} /></div>
							) : (
								<TanStackTable
									columnData={UNMINED_COLUMNS}
									rowData={propertyStatus.unminedRows}
									showSearch={false}
									defaultSort={[{ id: "property", desc: false }]}
								/>
							)
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default UnitMiningDashboard;
